import BaseSessionService from 'ember-simple-auth/services/session';
import { service } from '@ember/service';
import type CurrentUserService from './current-user';
import type FavoriteService from './favorite';
import type FetchService from '@triptyk/ember-utils/services/fetch';
import { BackendAdapter } from './favorite-adapters/backend';
import * as Sentry from '@sentry/ember';
import type UserModel from 'frontend/models/user';
import type FastbootService from 'ember-cli-fastboot';

export default class SessionService extends BaseSessionService {
  @service declare currentUser: CurrentUserService;
  @service declare favorite: FavoriteService;
  @service declare fetch: FetchService;
  @service declare fastboot: FastbootService;

  invalidate(): Promise<void> {
    this.invalidateSentryUser();
    return super.invalidate();
  }

  private invalidateSentryUser() {
    if (!this.fastboot.isFastBoot) {
      Sentry.setUser(null);
    }
  }

  private setSentryUser(user: UserModel) {
    if (!this.fastboot.isFastBoot) {
      Sentry.setUser({
        email: user.email,
        id: user.id,
        username: `${user.firstName} ${user.lastName}`,
      });
    }
  }

  async handleAuthentication() {
    try {
      const user = (await this.currentUser.load()) as UserModel;
      await this.useOnlineAdapter();
      this.setSentryUser(user);
    } catch (err) {
      /**
       * Does not invalidate with abord error, loading the current user is optional and may be canceled
       * `err instanceof AbortError` is false BTW, we check with the message
       */
      if ((err as Error).message === 'Aborted') {
        return;
      }
      await this.invalidate();
    }
  }

  public async useOnlineAdapter() {
    await this.favorite.switchAdapter(new BackendAdapter(this.fetch));
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    session: SessionService;
  }
}
